import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText, Users, TrendingUp, Wallet, ArrowRight, Sparkles,
  Star, ShieldCheck, Activity, Zap,
} from "lucide-react";
import { MODULE_CONFIG } from "@/components/layout/Sidebar";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const formatEur = (n) =>
  new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

const READY_MODULES = new Set([
  "invoicing", "crm", "accounting", "hr", "inventory",
  "restaurant", "auto_repair", "fleet", "logistics",
  "taxi", "car_rental", "money_transfer", "ereceipt", "projects", "quotes",
]);

const statusBadge = (s) => {
  const map = {
    draft: { label: "Luonnos", cls: "bg-zinc-700/40 text-zinc-200" },
    sent: { label: "Lähetetty", cls: "bg-blue-600/30 text-blue-200" },
    paid: { label: "Maksettu", cls: "bg-emerald-600/30 text-emerald-200" },
    overdue: { label: "Erääntynyt", cls: "bg-red-600/30 text-red-200" },
    cancelled: { label: "Peruutettu", cls: "bg-zinc-600/30 text-zinc-300" },
  };
  return map[s] || map.draft;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const userModules = user?.modules || [];
  const visibleModules = userModules.filter((m) => MODULE_CONFIG[m]);
  const readyCount = visibleModules.filter((m) => READY_MODULES.has(m)).length;
  const totalModules = visibleModules.length;
  const freeCount = Math.max(0, totalModules - readyCount);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero header */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="chip-pro">PREMIUM</span>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40">Apps2Go Hub</p>
          </div>
          <h1 className="text-3xl lg:text-5xl font-display font-bold tracking-tight gradient-text-luxe">
            Hallintapaneeli
          </h1>
          <p className="text-white/60 text-sm">
            Tervetuloa takaisin, <span className="text-amber-300 font-medium">{user?.full_name}</span>.
            Sinulla on <span className="text-white font-semibold">{readyCount}</span> aktiivista sovellusta käytössä.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/invoicing/new">
            <button data-testid="dashboard-new-invoice" className="btn-luxe inline-flex items-center gap-2">
              <FileText className="w-4 h-4" /> Uusi lasku
            </button>
          </Link>
          <Link to="/crm/new">
            <button
              data-testid="dashboard-new-customer"
              className="px-5 py-2.5 rounded-full glass-soft text-white/80 hover:text-white hover:border-amber-400/30 transition text-sm font-medium inline-flex items-center gap-2"
            >
              <Users className="w-4 h-4" /> Uusi asiakas
            </button>
          </Link>
        </div>
      </header>

      {error && <div className="text-destructive text-sm">{error}</div>}

      {/* KPI Stat blocks */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Wallet className="w-4 h-4" />}
          label="Liikevaihto yht."
          value={formatEur(stats?.revenue_total)}
          accent="amber"
          loading={loading}
          testid="kpi-revenue-total"
        />
        <StatCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Maksettu"
          value={formatEur(stats?.revenue_paid)}
          accent="emerald"
          loading={loading}
          testid="kpi-revenue-paid"
        />
        <StatCard
          icon={<FileText className="w-4 h-4" />}
          label="Avoinna"
          value={formatEur(stats?.revenue_open)}
          accent="blue"
          loading={loading}
          testid="kpi-revenue-open"
        />
        <StatCard
          icon={<Users className="w-4 h-4" />}
          label="Asiakkaita"
          value={stats?.customers_total ?? "—"}
          accent="violet"
          loading={loading}
          testid="kpi-customers"
        />
      </div>

      {/* Revenue chart + recent invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-card lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1">Trendit</p>
              <h3 className="text-lg font-display font-semibold text-white">Liikevaihto · viimeiset 6 kk</h3>
            </div>
            <Activity className="w-5 h-5 text-amber-400" />
          </div>
          <div className="h-64 min-h-[256px] w-full relative z-10">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={stats?.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.5)" fontSize={11} />
                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(20,10,55,0.95)",
                    border: "1px solid rgba(245,197,66,0.3)",
                    borderRadius: 12,
                    backdropFilter: "blur(20px)",
                  }}
                  formatter={(v) => formatEur(v)}
                  labelStyle={{ color: "#f5c542" }}
                />
                <Bar dataKey="total" fill="url(#luxeGrad)" radius={[8, 8, 0, 0]} />
                <defs>
                  <linearGradient id="luxeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f5c542" />
                    <stop offset="100%" stopColor="#b478ff" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1">Tuoreimmat</p>
              <h3 className="text-lg font-display font-semibold text-white">Laskut</h3>
            </div>
            <Link to="/invoicing" className="text-xs text-amber-300 hover:underline flex items-center gap-1">
              Kaikki <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="relative z-10 space-y-2">
            {(stats?.recent_invoices || []).length === 0 ? (
              <div className="text-sm text-white/40 py-6 text-center">Ei vielä laskuja</div>
            ) : (
              (stats?.recent_invoices || []).slice(0, 5).map((inv) => {
                const sb = statusBadge(inv.status);
                return (
                  <Link
                    key={inv.id}
                    to={`/invoicing/${inv.id}`}
                    data-testid={`recent-invoice-${inv.invoice_number}`}
                    className="block rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-amber-400/20 transition px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white/90 truncate">
                          #{inv.invoice_number} · {inv.recipient_name}
                        </p>
                        <p className="text-xs text-white/40">{inv.invoice_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-amber-300">{formatEur(inv.total)}</p>
                        <Badge className={`${sb.cls} border-0 text-[9px] mt-0.5`}>{sb.label}</Badge>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Module hero section */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">Discover your next tool</p>
            </div>
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-white mt-1">
              Sovellukset
            </h2>
          </div>

          {/* Mini stats inline like the reference */}
          <div className="hidden md:flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-display font-bold text-white">{totalModules}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Sovellusta</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-display font-bold text-emerald-400">{readyCount}</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Käytössä</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {visibleModules.map((m) => {
            const mod = MODULE_CONFIG[m];
            const Icon = mod.icon;
            const isReady = READY_MODULES.has(m);
            return (
              <Link
                key={m}
                to={mod.path}
                data-testid={`module-card-${m}`}
                className="module-card flex flex-col gap-3 group"
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.hue} flex items-center justify-center shadow-lg`}
                    style={{ boxShadow: "0 8px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.2)" }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {isReady ? (
                    <span className="chip-free">Käytössä</span>
                  ) : (
                    <span className="chip-pro">Pian</span>
                  )}
                </div>
                <div className="flex-1 relative z-10">
                  <p className="text-sm font-semibold text-white tracking-tight">{mod.label}</p>
                  <p className="text-[10px] text-white/40 mt-0.5">
                    {isReady ? "Aktiivinen moduuli" : "Tulossa pian"}
                  </p>
                </div>
                <div className="relative z-10 flex items-center justify-between pt-2 border-t border-white/5">
                  <span className="text-[10px] uppercase tracking-widest text-white/50 group-hover:text-amber-300 transition">
                    Avaa
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-white/40 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Bottom stat strip (like the reference's Difficulty / Position row) */}
      <section className="glass-card p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
          <BottomStat
            label="Sovelluksia"
            value={totalModules}
            icon={<Zap className="w-4 h-4" />}
            accent="from-amber-400 to-orange-500"
          />
          <BottomStat
            label="Käytössä"
            value={readyCount}
            icon={<Star className="w-4 h-4" />}
            accent="from-emerald-400 to-teal-600"
            highlight
          />
          <BottomStat
            label="Tulossa pian"
            value={freeCount}
            icon={<Sparkles className="w-4 h-4" />}
            accent="from-violet-400 to-purple-600"
          />
          <BottomStat
            label="2FA-suojaus"
            value={user?.twofa_enabled ? "Aktiivinen" : "Ei käytössä"}
            icon={<ShieldCheck className="w-4 h-4" />}
            accent="from-indigo-400 to-blue-600"
            small
          />
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, accent, loading, testid }) {
  const accentMap = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    blue: "text-blue-300",
    violet: "text-violet-300",
  };
  const accentBg = {
    amber: "from-amber-400/20 to-orange-500/10",
    emerald: "from-emerald-400/20 to-teal-500/10",
    blue: "from-blue-400/20 to-cyan-500/10",
    violet: "from-violet-400/20 to-purple-500/10",
  };
  return (
    <div className="stat-block group" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accentBg[accent]} flex items-center justify-center ${accentMap[accent]} border border-white/5`}>
          {icon}
        </div>
        <TrendingUp className="w-3.5 h-3.5 text-emerald-400/60" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className={`text-2xl lg:text-3xl font-display font-bold mt-1 ${accentMap[accent]}`}>
        {loading ? "…" : value}
      </p>
    </div>
  );
}

function BottomStat({ label, value, icon, accent, highlight, small }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white flex-shrink-0`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
        <p
          className={`font-display font-bold tracking-tight ${
            small ? "text-sm text-white" : highlight ? "text-2xl text-emerald-300" : "text-2xl text-white"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
